import type { RadioOptionSchemaMismatch } from '../../components/oasis/bespoke/layout/radioOptionSchemaCoverage';
import type { LayoutPlainCssHiddenQid } from '../../components/oasis/bespoke/layout/collectLayoutPlainCssHiddenQids';
import type { LayoutSchemaGateIssue } from '../../components/oasis/bespoke/layout/collectLayoutSchemaGateIssues';
import {
  formatLayoutPlainCssHiddenSummary,
  formatRadioOptionSchemaMismatchSummary,
} from './formSchemaUtils';
import { formatLayoutSchemaGateIssueSummary } from '../../components/oasis/bespoke/layout/collectLayoutSchemaGateIssues';

/** User message sent when requesting an LLM pass to repair coverage gaps. */
export function buildCoverageFixAssistantPrompt(issues: {
  uncoveredQids: string[];
  strayQids: string[];
  radioMismatches: RadioOptionSchemaMismatch[];
  cssHiddenQids: LayoutPlainCssHiddenQid[];
  missingCmsVisibleLabels: string[];
  schemaGateIssues: LayoutSchemaGateIssue[];
  parseOk?: boolean;
}): string {
  const lines: string[] = [
    'Fix the Roger Layout Schema for this section so coverage validation passes.',
    '',
    'Update the layout JSON to:',
    '- Add a proper answer control for every required schema QID that is still missing from the layout.',
    '- Remove or replace references to QIDs that are not part of this section schema (only use QIDs from the bundled section schema).',
    '- For radio questions rendered as tables with `radio-option` rows: each row code must match the schema values[] for that QID exactly—add missing codes and remove extras.',
    '- Do not hide required answer controls with CSS (`hidden`, `invisible`, `opacity-0`, `display: none`, `visibility: hidden`, `opacity: 0`) on field nodes or ancestor wrappers.',
    '- Top-level schema questions must render outside `conditional` and `disabled-when` blocks. Nested subitem questions may sit inside those gates optionally; when gated, `{ qid, eq }` / `{ qid, includes }` clauses may only reference schema ancestors (parent or higher) with the schema subitems answer key. **`conditional.when` = show when true; `disabled-when.whenAll` = keep enabled while all clauses hold (disable when any fail). Use the same schema-path answer for both — do not invert the eq value.** Never wrap the gate control itself inside its own `disabled-when`.',
    '- For each lettered CMS item with layout controls: the lettered base id (e.g. `M0020`, `GG0100`) must appear in user-visible layout text (static, section-label, table header/row label, field labelOverride, or gated-row top static)—prefer parentheses, e.g. `(M0020) Medical Record Number`. Do not use cms_casting suffixes (`GG0100A`, `GG0100B`) on labels.',
    '- Avoid redundant copy: do not repeat a group heading phrase in every child option label (e.g. heading **Oriented to:** with options **Person** / **Place** / **Time**, not **Oriented to person** again).',
    '',
    'Reply with a corrected complete layout_json array.',
    '',
    'Current validation findings:',
  ];

  if (issues.parseOk === false) {
    lines.push('', 'The current layout JSON could not be parsed or validated.');
  }

  if (issues.uncoveredQids.length > 0) {
    lines.push(
      '',
      `Required QIDs missing from Roger Layout Schema (${issues.uncoveredQids.length}):`,
      issues.uncoveredQids.join(', ')
    );
  }
  if (issues.strayQids.length > 0) {
    lines.push(
      '',
      `QIDs present in Roger Layout Schema that do not belong (${issues.strayQids.length}):`,
      issues.strayQids.join(', ')
    );
  }
  if (issues.radioMismatches.length > 0) {
    lines.push(
      '',
      `Radio table codes vs schema values[] (${issues.radioMismatches.length}):`
    );
    for (const mismatch of issues.radioMismatches) {
      lines.push(`- ${formatRadioOptionSchemaMismatchSummary(mismatch)}`);
    }
  }
  if (issues.cssHiddenQids.length > 0) {
    lines.push(
      '',
      `Required QIDs hidden with CSS (${issues.cssHiddenQids.length}):`
    );
    for (const hidden of issues.cssHiddenQids) {
      lines.push(`- ${formatLayoutPlainCssHiddenSummary(hidden)}`);
    }
  }
  if (issues.schemaGateIssues.length > 0) {
    lines.push(
      '',
      `Schema parent/child gating (${issues.schemaGateIssues.length}):`
    );
    for (const gateIssue of issues.schemaGateIssues) {
      lines.push(`- ${formatLayoutSchemaGateIssueSummary(gateIssue)}`);
    }
  }
  if (issues.missingCmsVisibleLabels.length > 0) {
    lines.push(
      '',
      `Lettered CMS item ids missing visible layout labels (${issues.missingCmsVisibleLabels.length}) — add in parentheses, e.g. (GG0100):`,
      issues.missingCmsVisibleLabels.map(id => `(${id})`).join(', ')
    );
  }

  return lines.join('\n');
}

/** Compact JSON for vibecoder API payloads; editor UI may keep pretty-printed text. */
export function compactJsonTextForVibecoderApi(text: string): string {
  try {
    return JSON.stringify(JSON.parse(text));
  } catch {
    return text;
  }
}

export function layoutJsonTextForCoverageFix(
  layoutJson: unknown | null | undefined
): string {
  if (layoutJson == null) return '[]';
  try {
    return JSON.stringify(layoutJson);
  } catch {
    return '[]';
  }
}
